import { z } from 'zod';
import { ANALYST_STATUSES, FOLLOW_STATUSES } from '../../ontology/objects/analyst.ts';
import { alertFloorsSchema, CHANNEL_KINDS } from '../../ontology/objects/channel.ts';
import { MEMBER_STATUSES } from '../../ontology/objects/member.ts';
import { idSchema } from '../../ontology/primitives.ts';
import { day, instant } from './primitives.ts';
import { bookSettingsSchema } from './recommendation.ts';

export const wireMemberSchema = z.object({
  id: idSchema,
  email: z.string(),
  name: z.string().nullable(),
  picture: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  requestedAt: instant,
  decidedAt: instant.nullable(),
  decidedBy: z.string().nullable(),
  /** The desk sees only this, never the key. */
  brokerLinked: z.boolean(),
  /** `failure`: why the desk could not make their app. */
  brokerAsk: z.object({ zerodhaId: z.string(), failure: z.string().nullable() }).nullable(),
  invitedBy: z
    .object({
      name: z.string(),
      memberSince: instant.nullable(),
      tally: z.object({
        PENDING: z.number().int(),
        APPROVED: z.number().int(),
        REJECTED: z.number().int(),
      }),
    })
    .nullable(),
});

export type WireMember = z.infer<typeof wireMemberSchema>;

/** Sent only to the analyst and the desk. The webhook digest is never sent. */
const wireChannelSchema = z.object({
  id: idSchema,
  kind: z.enum(CHANNEL_KINDS),
  label: z.string(),
  telegramChatId: z.number().int().nullable(),
  webhookMintedAt: instant.nullable(),
  autoFile: z.boolean(),
  book: z.string().nullable(),
  ...alertFloorsSchema.shape,
});

export type WireChannel = z.infer<typeof wireChannelSchema>;

const wireFollowerSchema = z.object({
  followId: idSchema,
  memberId: idSchema,
  name: z.string().nullable(),
  email: z.string(),
  picture: z.string().nullable(),
  status: z.enum(FOLLOW_STATUSES),
  requestedAt: instant,
});

export type WireFollower = z.infer<typeof wireFollowerSchema>;

const wireFollowInviteSchema = z.object({
  id: idSchema,
  email: z.string().nullable(),
  /** Null once used, withdrawn or expired: a spent link is nothing to copy. */
  link: z.string().nullable(),
  createdAt: instant,
  expiresAt: instant,
  revokedAt: instant.nullable(),
  usedAt: instant.nullable(),
  usedBy: z.string().nullable(),
});

export type WireFollowInvite = z.infer<typeof wireFollowInviteSchema>;

export const wireAnalystSchema = z.object({
  id: idSchema,
  name: z.string(),
  bio: z.string().nullable(),
  status: z.enum(ANALYST_STATUSES),
  books: z.array(
    z.object({
      analystId: idSchema,
      bookId: idSchema,
      book: z.string(),
      ...bookSettingsSchema.shape,
    }),
  ),
  /** What each book holds, by `bookId`. Empty unless the viewer is the analyst or the desk. */
  shelf: z.array(
    z.object({
      bookId: idSchema,
      openedAt: instant,
      open: z.number().int(),
      closed: z.number().int(),
    }),
  ),
  followers: z.number().int(),
  mine: z.object({ followId: idSchema, status: z.enum(FOLLOW_STATUSES) }).nullable(),
  claimed: z.boolean(),
  decides: z.boolean(),
  picture: z.string().nullable(),
  channels: z.array(wireChannelSchema),
  requestedAt: instant,
  decidedAt: instant.nullable(),
  roster: z.array(wireFollowerSchema),
  listed: z.boolean(),
  /** The viewer's exits, per call: the stream caps exited positions. */
  banked: z.array(z.object({ recommendationId: idSchema, rupees: z.number() })),
  /** Empty unless the viewer decides who follows them: an open link lets somebody in. */
  invites: z.array(wireFollowInviteSchema),
});

export type WireAnalyst = z.infer<typeof wireAnalystSchema>;

/** The only response carrying the credential: just a digest is stored, so a lost URL means re-minting. */
export const webhookMintSchema = z.object({
  url: z.string(),
  mintedAt: instant,
  channel: wireChannelSchema,
});

export type WebhookMint = z.infer<typeof webhookMintSchema>;

export const analystsReplySchema = z.object({
  analysts: z.array(wireAnalystSchema),
  /** Nifty 50's daily closes, what a call is read against for alpha and beta. */
  market: z.array(z.object({ day, close: z.number() })),
});

export type AnalystsReply = z.infer<typeof analystsReplySchema>;

export const ontologySchema = z.object({
  objectTypes: z.array(z.object({ apiName: z.string(), description: z.string() })),
  actionTypes: z.array(
    z.object({ apiName: z.string(), objectType: z.string(), description: z.string() }),
  ),
});

export type OntologyBody = z.infer<typeof ontologySchema>;
